/**
 * Shared weather primitives — used by:
 *   • Home dashboard (widget card)
 *   • Personal Map (chip + route timeline strip)
 *
 * All rendering is dumb — data comes from the caller so the same component
 * works with any coordinate (current location, destination, route sample).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useOnline } from "@/lib/useOnline";
import { CachedBadge } from "@/components/OfflineBanner";

// WMO code → emoji. Keeps the surface icon-agnostic so we can swap for
// lucide/font-awesome later without touching callers.
export function weatherEmoji(iconKey) {
  switch (iconKey) {
    case "sun":                 return "☀️";
    case "cloud-sun":           return "⛅";
    case "cloud-rain":
    case "cloud-rain-wind":     return "🌧️";
    case "cloud-showers-heavy": return "⛈️";
    case "cloud-drizzle":       return "🌦️";
    case "bolt":                return "⚡";
    case "snowflake":           return "❄️";
    case "cloud-fog":           return "🌫️";
    default:                    return "☁️";
  }
}

/**
 * Fetches current weather for the given lat/lng and renders a card in the
 * same visual language as the other Home widgets. Falls back gracefully if
 * the API is unavailable (renders a subtle "unavailable" state).
 */
export function WeatherCard({ lat, lng, cityLabel, to = "/nearby" }) {
  const [wx, setWx] = useState(null);
  const [err, setErr] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const { online } = useOnline();

  useEffect(() => {
    if (typeof lat !== "number" || typeof lng !== "number") return;
    if (!online) return;   // don't hammer /weather/current while offline
    let cancelled = false;
    setErr(false);
    api.get("/weather/current", { params: { lat, lng } })
       .then(r => {
         if (cancelled) return;
         setWx(r.data);
         setFetchedAt(new Date().toISOString());
       })
       .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [lat, lng, online]);

  const good = wx && wx.available;
  const stale = !online && !!wx;    // we have data but we're currently offline
  return (
    <Link
      to={to}
      data-testid="home-weather-card"
      className="group rounded-3xl p-4 bg-gradient-to-br from-sky-500/90 to-indigo-800/90 border border-white/10 backdrop-blur text-white shadow-xl hover:-translate-y-0.5 transition-transform"
    >
      <div className="flex items-center justify-between">
        <span className="text-2xl leading-none" aria-hidden>
          {good ? weatherEmoji(wx.condition?.icon) : "☁️"}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Weather</span>
      </div>
      <div className="mt-2 text-3xl font-black tabular-nums" data-testid="home-weather-temp">
        {good ? `${Math.round(wx.temp_c ?? 0)}°C` : (err ? "—" : "…")}
      </div>
      <div className="mt-1 text-[11px] text-white/70 flex items-center gap-1 truncate">
        {good ? (
          <>
            <span className="truncate">{wx.condition?.label}</span>
            {cityLabel && <span className="text-white/50">· {cityLabel}</span>}
          </>
        ) : err ? "Try again later" : (online ? "Fetching latest conditions…" : "Offline — waiting for connection")}
      </div>
      {stale && (
        <div className="mt-2">
          <CachedBadge updatedAt={fetchedAt} data-testid="weather-cached-badge"/>
        </div>
      )}
    </Link>
  );
}

/**
 * Horizontal rain-probability strip for a route. Each sample is a bar tinted
 * by its precip probability. Tapping a bar surfaces the offset/time + condition
 * in a tooltip. Data is fetched by the caller (we accept the response verbatim
 * so the caller can cache/manage it).
 */
export function RouteWeatherStrip({ timeline, className = "" }) {
  if (!timeline?.available || !timeline.points?.length) return null;
  const pts = timeline.points;
  return (
    <div className={`rounded-2xl bg-slate-800/60 border border-white/10 p-2 ${className}`}
         data-testid="route-weather-strip">
      <div className="flex items-center gap-1 mb-1">
        <div className="text-[10px] uppercase font-black text-sky-200">Rain along route</div>
        <div className="text-[10px] text-white/50 flex-1 text-right">
          peak {timeline.summary?.max_precip_prob_pct ?? 0}%
        </div>
      </div>
      <div className="flex items-end gap-1 h-10">
        {pts.map((p, i) => {
          const prob = p.precip_prob_pct ?? 0;
          const h = Math.max(6, Math.round(prob * 0.4 + 6));   // 6-46 px
          const color = prob >= 70 ? "bg-rose-400"
                      : prob >= 40 ? "bg-amber-300"
                      : prob >= 15 ? "bg-sky-300"
                      : "bg-emerald-300";
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5"
                 data-testid={`route-wx-bar-${i}`}
                 title={`+${p.offset_min} min · ${p.condition?.label} · ${prob}% rain · ${Math.round(p.temp_c ?? 0)}°C`}>
              <div className={`w-full rounded-t ${color}`} style={{ height: `${h}px` }} />
              <div className="text-[8.5px] font-mono text-white/60">{prob}%</div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[8.5px] uppercase font-black text-white/40 mt-1">
        <span>A · start</span>
        <span>B · +{Math.round(timeline.duration_min || 0)}m</span>
      </div>
    </div>
  );
}
